import { defineMessages } from '@atlassian/jira-intl';
import type { Metric } from './EntityKey.types.tsx';

const messages = defineMessages({
	name: {
		id: 'admin-pages-performance.common.assets.issue.name',
		defaultMessage: 'Issues',
		description:
			'Name of the site optimizer metric that will be used as the title on the page and menu item.',
	},

	nameV2: {
		id: 'admin-pages__performance.common.assets.issue.name-v-2',
		defaultMessage: 'Work items',
		description:
			'Name of the site optimizer metric that will be used as the title on the page and menu item.',
	},

	description: {
		id: 'admin-pages-performance.common.assets.issue.description',
		defaultMessage:
			'Here’s a quick snapshot of the total number of issues in your site, including some that we think can be archived. Archiving unused issues can improve performance and stability of your site.',
		description: 'Description of the site optimizer metric.',
	},

	reviewAndAnalyze: {
		id: 'admin-pages-performance.common.assets.issue.review-and-analyze',
		defaultMessage: 'Analysis',
		description: 'A tab label of Issue Tabs, to show issue review and analyze',
	},

	activityLog: {
		id: 'admin-pages-performance.common.assets.issue.activity-log',
		defaultMessage: 'Activity log',
		description: 'A tab label of Activity Log Tabs, to show Issue Archival past operations',
	},

	warning: {
		id: 'admin-pages-performance.common.assets.issue.warning',
		defaultMessage: 'The number of issues in the system is nearing our recommended threshold.',
		description: 'Warning message if metric are getting close to the threshold.',
	},

	exceeded: {
		id: 'admin-pages-performance.common.assets.issue.exceeded',
		defaultMessage: 'There are more issues in the system than our recommended threshold.',
		description: 'Message to show to the user if metric has exceeded the threshold.',
	},

	risk1: {
		id: 'admin-pages-performance.common.assets.issue.risk-1',
		defaultMessage:
			'If you have too many issues you may find searches, automations, and workflow rules are slower than usual.',
		description: 'One of the risks when this metric exceeded the threshold.',
	},

	mitigation1: {
		id: 'admin-pages-performance.common.assets.issue.mitigation-1',
		defaultMessage: 'Delete any projects that are no longer required.',
		description: 'One of the mitigation options when this metric exceeded the threshold.',
	},

	mitigation2: {
		id: 'admin-pages-performance.common.assets.issue.mitigation-2',
		defaultMessage: "Disable Jira integrations that create issues you don't need.",
		description: 'One of the mitigation options when this metric exceeded the threshold.',
	},

	mitigation3: {
		id: 'admin-pages-performance.common.assets.issue.mitigation-3',
		defaultMessage:
			// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
			'Delete old issues that are no longer required. Use a JQL query to find issues older than a year (“created < -365d order by created DESC“).',
		description: 'One of the mitigation options when this metric exceeded the threshold.',
	},

	currentValueLabel: {
		id: 'admin-pages-performance.common.assets.issue.current-value-label',
		defaultMessage: 'Total current issues in your site',
		description: 'Label to be shown next to the current value of the metric.',
	},

	thresholdValueLabel: {
		id: 'admin-pages-performance.common.assets.issue.threshold-value-label',
		defaultMessage: 'Recommended maximum number of issues',
		description: 'Label to be shown next to the threshold value of the metric.',
	},

	currentValueAriaLabel: {
		id: 'admin-pages-performance.common.assets.issue.current-value-aria-label',
		defaultMessage: '{number} total current issues in your site',
		description: 'Aria Label for the current value',
	},

	thresholdValueAriaLabel: {
		id: 'admin-pages-performance.common.assets.issue.threshold-value-aria-label',
		defaultMessage: '{number} recommended maximum number of issues',
		description: 'Aria Label for the threshold value',
	},

	graphHeader: {
		id: 'admin-pages-performance.common.assets.issue.graph-header',
		defaultMessage: 'Issues over time',
		description: 'Text to be shown on the header of the metric graph.',
	},
});

export const ISSUE_KEY = 'issue';

export const ISSUE: Metric = {
	key: ISSUE_KEY,
	visible: true,
	name: messages.name,
	nameV2: messages.nameV2,
	description: messages.description,
	tabLabels: [messages.reviewAndAnalyze, messages.activityLog],
	warningMessage: messages.warning,
	exceededMessage: messages.exceeded,
	risks: [messages.risk1],
	mitigations: [messages.mitigation1, messages.mitigation2, messages.mitigation3],
	currentValueLabel: messages.currentValueLabel,
	currentValueAriaLabel: messages.currentValueAriaLabel,
	thresholdValueLabel: messages.thresholdValueLabel,
	thresholdValueAriaLabel: messages.thresholdValueAriaLabel,
	graphHeader: messages.graphHeader,
};
